import { message } from "antd";
import { partition } from "lodash-es";
import { useMemo } from "react";

import { BdsSelect } from "@buildertrend/components";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { MfaConstants } from "types/constants";

import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect, type BTSelectProps } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import {
    type IRoleExtraData,
    unselectedRoleDropdownId,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

export interface IRoleSelectProps<FormValues> extends BTSelectProps<FormValues, IRoleExtraData> {
    useBds?: boolean;
}

const createGroupedRoleTreeData = (roleTreeData: BTSelectItem<IRoleExtraData>[]) => {
    const td: BTSelectItem<IRoleExtraData>[] = [];
    if (
        roleTreeData.find((role) => role.selected && parseInt(role.id) === unselectedRoleDropdownId)
    ) {
        td.push(
            new BTSelectItem<IRoleExtraData>({
                id: unselectedRoleDropdownId,
                name: "-- Choose Role --",
                selected: true,
                extraData: undefined,
            }),
        );
    }
    roleTreeData = roleTreeData.filter((r) => parseInt(r.id) !== unselectedRoleDropdownId);
    const [standardRoleGroup, customRoleGroup] = partition(
        roleTreeData,
        (r) => r.extraData?.isSystemDefault,
    );

    td.push(
        new BTSelectItem<IRoleExtraData>({
            id: "standardRoleGroup",
            title: "Standard Roles",
            children: standardRoleGroup,
        }),
    );

    if (customRoleGroup.length > 0) {
        td.push(
            new BTSelectItem<IRoleExtraData>({
                id: "customRoleGroup",
                title: "Custom Roles",
                children: customRoleGroup,
            }),
        );
    }

    return td;
};

export const RoleSelect = <FormValues,>(props: IRoleSelectProps<FormValues>) => {
    const { disabled, treeData, readOnly, useBds } = props;

    const groupedTreeData = useMemo(() => createGroupedRoleTreeData(treeData ?? []), [treeData]);

    const areAllRoleOptionsDisabled = useMemo(
        () =>
            disabled ||
            groupedTreeData.every(
                (item) =>
                    Array.isArray(item.children) && item.children.every((child) => child.disabled),
            ),
        [disabled, groupedTreeData],
    );

    const customRoleItemRender = (item: BTSelectItem<IRoleExtraData>) => {
        return (
            <BTRow
                align="middle"
                onClick={() =>
                    !readOnly &&
                    item.extraData?.disabledMessage &&
                    message.info(item.extraData?.disabledMessage)
                }
            >
                {item.title}
                {item.extraData?.isMfaEnabled && (
                    <StatusTagDisplay
                        statusText={MfaConstants.MfaRequired}
                        statusType={"info"}
                        className="margin-left-xs"
                    />
                )}
            </BTRow>
        );
    };

    const SelectComponent = useBds ? BdsSelect : BTSelect;
    return (
        <SelectComponent<FormValues, IRoleExtraData>
            {...props}
            treeData={groupedTreeData}
            customItemRender={customRoleItemRender}
            disabled={areAllRoleOptionsDisabled}
            readOnly={readOnly}
        />
    );
};
